import { query } from 'bitecs';
import { Scene, WebGPURenderer, Color } from 'three/webgpu';
import { viewportSize } from '@src/boot/fullscreen';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { Anim, BossTag, DragonTag, Dying, Enemy, EnemyKind, Health, Player, Position, PrevPosition, Radius, Spawner, Sprite, SpriteId, Velocity, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { RoomPhase, type GameContext } from '@src/ecs/world';
import { isFreeRoom } from '@src/game/arena/RoomLayout';
import { Anims } from '@src/game/AnimDefs';
import { bossDef } from '@src/game/BossDefs';
import { deathVisual, sampleDeathVisual } from '@src/game/DeathFx';
import { weaponDef } from '@src/game/weapons/WeaponDefs';
import type { Atlas } from '@src/graphics/AtlasLoader';
import { floorThemeDef, type ArenaArt } from '@src/graphics/ArenaArt';
import { ArenaCamera } from '@src/graphics/Camera';
import { ArenaScene } from '@src/graphics/ArenaScene';
import { BeamPool } from '@src/graphics/BeamPool';
import { DecalPool } from '@src/graphics/DecalPool';
import { ParticlePool } from '@src/graphics/ParticlePool';
import { PortalAura } from '@src/graphics/PortalAura';
import { ShapeKind, SpritePool } from '@src/graphics/SpritePool';

interface PoolSpec {
  capacity: number;
  additive: boolean;
  order: number;
  shape: ShapeKind;
  /** Procedural-shape width/height when no atlas frame exists. */
  aspect: number;
  /** Radians to add so the atlas art points along +X (for directional sprites). */
  rotOffset: number;
  /** Front-view character art: never rotate, flip horizontally to face. */
  faceFlip: boolean;
  /** Crowd kinds: y-sort instances so bodies lower on screen draw over the ones behind. */
  ySort?: boolean;
}

const QUARTER = Math.PI / 4;
const FX_CAP = Config.pools.fx;

const POOL_SPECS: Readonly<Record<SpriteId, PoolSpec>> = {
  [SpriteId.Player]: { capacity: 12, additive: false, order: 10, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true },
  [SpriteId.Imp]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Golem]: { capacity: Config.pools.golems, additive: false, order: 9, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Skull]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Slime]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Bat]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Wraith]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Hexer]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Cinder]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Blight]: { capacity: Config.pools.imps, additive: false, order: 9, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Brute]: { capacity: Config.pools.golems, additive: false, order: 9, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.Spitter]: { capacity: Config.pools.gargoyles, additive: false, order: 8, shape: ShapeKind.Square, aspect: 0.85, rotOffset: 0, faceFlip: true },
  [SpriteId.Mimic]: { capacity: 8, additive: false, order: 9, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.PickupHeart]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Portal]: { capacity: Config.pools.spawners * 3, additive: false, order: 6, shape: ShapeKind.Portal, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Dragon]: { capacity: 2, additive: false, order: 14, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BoltWand]: { capacity: Config.pools.projectiles, additive: true, order: 11, shape: ShapeKind.Bolt, aspect: 2.2, rotOffset: 0, faceFlip: false },
  [SpriteId.BoltFire]: { capacity: Config.pools.projectiles, additive: true, order: 11, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BoltPhase]: { capacity: 1200, additive: true, order: 11, shape: ShapeKind.Bolt, aspect: 3, rotOffset: -QUARTER, faceFlip: false },
  [SpriteId.Shockwave]: { capacity: 64, additive: true, order: 11, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PickupCleaver]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PickupLightning]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PickupCrossbow]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PickupBomb]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PickupShield]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.WardBlade]: { capacity: 16, additive: true, order: 11, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Mine]: { capacity: 16, additive: false, order: 5, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Gem]: { capacity: Config.pools.pickups, additive: true, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeCabbage]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeHat]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeChalice]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeGoldChest]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeSpellbook]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeCrystalBall]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizePotion]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.PrizeHourglass]: { capacity: Config.pools.pickups, additive: false, order: 7, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Ghost]: { capacity: 4, additive: false, order: 12, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true },
  [SpriteId.Shadow]: { capacity: Config.pools.imps + Config.pools.golems + Config.pools.gargoyles + Config.pools.bosses + Config.pools.pickups + 24, additive: false, order: 4, shape: ShapeKind.Shadow, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.MuzzleWand]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.MuzzleFire]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.MuzzlePhase]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Slash]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.LightningHit]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Explosion]: { capacity: FX_CAP, additive: true, order: 13, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BombBlast]: { capacity: FX_CAP, additive: true, order: 13, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Goo]: { capacity: FX_CAP, additive: false, order: 8, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Smoke]: { capacity: FX_CAP, additive: false, order: 12, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Spark]: { capacity: FX_CAP, additive: true, order: 12, shape: ShapeKind.Diamond, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.Torch]: { capacity: 32, additive: true, order: 5, shape: ShapeKind.Wisp, aspect: 0.6, rotOffset: 0, faceFlip: false },
  [SpriteId.FlameJet]: { capacity: 8, additive: true, order: 13, shape: ShapeKind.Bolt, aspect: 3, rotOffset: 0, faceFlip: false },
  [SpriteId.Gargoyle]: { capacity: Config.pools.gargoyles, additive: false, order: 8, shape: ShapeKind.Square, aspect: 0.85, rotOffset: 0, faceFlip: true },
  [SpriteId.BossLich]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Wisp, aspect: 0.8, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossDruid]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Diamond, aspect: 0.8, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossMagus]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Square, aspect: 0.85, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossBolt]: { capacity: Config.pools.projectiles, additive: true, order: 11, shape: ShapeKind.Bolt, aspect: 2.2, rotOffset: 0, faceFlip: false },
  [SpriteId.BossOrb]: { capacity: Config.pools.projectiles, additive: true, order: 11, shape: ShapeKind.Ring, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BossWisp]: { capacity: 256, additive: true, order: 11, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BossWarlord]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Square, aspect: 0.9, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossSkullKing]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Wisp, aspect: 0.9, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossColossus]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Circle, aspect: 1.1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossMatriarch]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Diamond, aspect: 1.3, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossSentinel]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Square, aspect: 0.85, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossHexweaver]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossAshmaw]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossBride]: { capacity: Config.pools.bosses, additive: false, order: 10, shape: ShapeKind.Wisp, aspect: 0.8, rotOffset: 0, faceFlip: true, ySort: true },
  [SpriteId.BossRubble]: { capacity: 256, additive: false, order: 11, shape: ShapeKind.Square, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BossAcid]: { capacity: 128, additive: true, order: 11, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
  [SpriteId.BossSkull]: { capacity: 128, additive: false, order: 11, shape: ShapeKind.Wisp, aspect: 1, rotOffset: 0, faceFlip: true },
  [SpriteId.COUNT]: { capacity: 0, additive: false, order: 0, shape: ShapeKind.Circle, aspect: 1, rotOffset: 0, faceFlip: false },
};

const HALF_PI = Math.PI / 2;
const OVERCHARGE_TINT = 0xfff2a0;
const SHADOW_TINT = 0x000000;
const PORTAL_WOUNDED = 0x7a6488;
const PORTAL_CRITICAL = 0x5a2030;

/**
 * Presentation-side system. Reads ECS typed arrays once per frame and writes
 * instance transforms into the per-archetype pools, then renders the scene.
 * Adds the procedural motion layer (bob, lean, squash, pop, recoil, shadows) on
 * top of the sprite-sheet frames chosen by AnimationSystem.
 */
export class RenderSyncSystem implements GameSystem {
  readonly renderer: WebGPURenderer;
  readonly scene = new Scene();
  readonly arenaCamera = new ArenaCamera();
  arena!: ArenaScene;
  /** Incoming room, parked beside the current one during a gate walk. */
  private nextArena: ArenaScene | null = null;
  private readonly incomingView = {
    tier: 1,
    phase: RoomPhase.Enter,
    gatesOpen: false,
    doorKinds: null as unknown as Uint8Array,
  };
  particles!: ParticlePool;
  private portalAura!: PortalAura;
  private ctx!: GameContext;
  private pools: SpritePool[] = [];
  private beams!: BeamPool;
  private decals!: DecalPool;
  private atlas: Atlas | null = null;
  private arenaArt: ArenaArt | null = null;
  private readonly canvas: HTMLCanvasElement;
  private readonly voidColor = new Color(0x05060a);
  private ready = false;
  private openingSlamPrimed = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new WebGPURenderer({ canvas, antialias: true, alpha: false });
  }

  /** Async GPU init. Must complete before init()/update(). */
  async setup(atlas: Atlas | null, arenaArt: ArenaArt | null): Promise<string> {
    this.atlas = atlas;
    this.arenaArt = arenaArt;
    await this.renderer.init();
    this.renderer.setClearColor(0x05060a, 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.scene.background = this.voidColor;
    this.ready = true;
    const backend = (this.renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend ? 'WebGPU' : 'WebGL2';
    return backend;
  }

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.arena = new ArenaScene(this.scene, this.arenaArt);
    this.decals = new DecalPool();
    this.scene.add(this.decals.mesh);
    this.beams = new BeamPool(ctx.beams.capacity, 0x7af6ff);
    this.scene.add(this.beams.mesh);
    this.particles = new ParticlePool(Config.pools.particles);
    this.scene.add(this.particles.additiveMesh);
    this.scene.add(this.particles.solidMesh);
    this.portalAura = new PortalAura();
    this.scene.add(this.portalAura.mesh);

    const atlasTex = this.atlas?.texture ?? null;
    for (let id = 0; id < SpriteId.COUNT; id++) {
      const spec = POOL_SPECS[id as SpriteId];
      const useArt = atlasTex !== null && Anims.hasArt(id as SpriteId);
      const pool = new SpritePool({
        capacity: spec.capacity,
        additive: spec.additive,
        renderOrder: spec.order,
        shape: spec.shape,
        frames: useArt ? Anims.frameTable(id as SpriteId) : null,
        atlas: useArt ? atlasTex : null,
        ySort: spec.ySort === true,
      });
      this.pools.push(pool);
      this.scene.add(pool.mesh);
    }

    window.addEventListener('resize', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);
    this.onResize();
  }

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    for (const p of this.pools) this.scene.remove(p.mesh);
    for (const p of this.pools) p.dispose();
    this.pools = [];
    this.particles.dispose();
    if (this.portalAura) {
      this.scene.remove(this.portalAura.mesh);
      this.portalAura.dispose();
    }
    if (this.nextArena) {
      this.nextArena.dispose(this.scene);
      this.nextArena = null;
    }
    this.renderer.dispose();
  }

  private ensureNextArena(): void {
    if (this.nextArena) return;
    const room = this.ctx.room;
    this.nextArena = new ArenaScene(this.scene, this.arenaArt, true);
    this.nextArena.setOffset(room.shiftX, room.shiftY);
  }

  private settleNextArena(): void {
    if (!this.nextArena) return;
    // The walk is over: the incoming room becomes the live arena at the origin.
    this.arena.dispose(this.scene);
    this.nextArena.setOffset(0, 0);
    this.arena = this.nextArena;
    this.nextArena = null;
  }

  private readonly onResize = (): void => {
    const { w, h } = viewportSize();
    this.renderer.setSize(w, h, false);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.arenaCamera.resize(w, h);
  };

  /** Called once per rendered frame; `alpha` interpolates between the last two ticks. */
  update(alpha: number, frameDt = 1 / 60): void {
    if (!this.ready) return;
    const ctx = this.ctx;
    this.drainDecals();
    this.particles.drain(ctx.fx);
    this.particles.update(frameDt);
    this.syncSprites(alpha);
    this.syncBeams();
    const room = ctx.room;
    const walking = room.phase === RoomPhase.Transition && (room.shiftX !== 0 || room.shiftY !== 0);
    if (walking) this.ensureNextArena();
    else if (this.nextArena) {
      if (room.phase === RoomPhase.Enter || room.phase === RoomPhase.Fight || room.phase === RoomPhase.Clear) {
        this.settleNextArena();
      } else {
        this.nextArena.dispose(this.scene);
        this.nextArena = null;
      }
    }
    this.arenaCamera.setCenter(room.panX, room.panY);
    const openingSlam = room.phase === RoomPhase.Enter && room.roomIndex === 1;
    if (openingSlam && !this.openingSlamPrimed) {
      this.arena.primeSlam();
      this.openingSlamPrimed = true;
    } else if (!openingSlam) {
      this.openingSlamPrimed = false;
    }
    const slamDur = openingSlam ? 0.55 : 0.35;
    const u = room.phase === RoomPhase.Enter ? Math.min(1, room.phaseTime / slamDur) : 1;
    const slam = openingSlam ? u * u : u;
    const floorNow = ctx.floor.floor;
    const outgoingFloor = walking && ctx.floor.isAtEntrance() && ctx.floor.clearedCount() === 0 && floorNow > 1 ? floorNow - 1 : floorNow;
    this.voidColor.setHex(floorThemeDef(outgoingFloor).surround);
    this.renderer.setClearColor(this.voidColor, 1);
    this.arena.update(room, slam, ctx.time, frameDt, walking ? room.chosenGate : -1, outgoingFloor);
    if (this.nextArena && walking) {
      this.incomingView.tier = room.tier + (isFreeRoom(room.door) ? 0 : 1);
      this.incomingView.phase = RoomPhase.Enter;
      this.incomingView.gatesOpen = false;
      this.incomingView.doorKinds = room.nextDoorKinds;
      this.nextArena.update(this.incomingView, 1, ctx.time, frameDt, room.entrySide, floorNow);
    }
    this.renderer.render(this.scene, this.arenaCamera.camera);
  }

  private drainDecals(): void {
    const fx = this.ctx.fx;
    const events = this.ctx.events;
    for (let i = 0; i < events.length; i++) {
      const e = events.peekAt(i);
      if (e.type === GameEvent.RoomNext) {
        this.decals.clear();
        this.particles.clear();
      }
    }
    for (let i = 0; i < fx.decalCount; i++) {
      const o = i * 4;
      this.decals.add(fx.decals[o], fx.decals[o + 1], fx.decals[o + 2], fx.decals[o + 3], this.ctx.rng.next() * Math.PI * 2);
    }
    fx.decalCount = 0;
  }

  private syncSprites(alpha: number): void {
    const world = this.ctx.world;
    const t = this.ctx.time;
    const pools = this.pools;
    const shadows = pools[SpriteId.Shadow];
    for (let i = 0; i < pools.length; i++) pools[i].begin();
    this.portalAura.begin();

    const drawables = query(world, [Sprite, Position, PrevPosition, Anim]);
    for (let i = 0; i < drawables.length; i++) {
      const eid = drawables[i];
      const id = Sprite.id[eid] as SpriteId;
      const pool = pools[id];
      if (!pool) continue;
      const spec = POOL_SPECS[id];
      let x = PrevPosition.x[eid] + (Position.x[eid] - PrevPosition.x[eid]) * alpha;
      let y = PrevPosition.y[eid] + (Position.y[eid] - PrevPosition.y[eid]) * alpha;
      let rot = Sprite.rot[eid] + spec.rotOffset;
      let tint = Sprite.tint[eid];
      let scale = Sprite.scale[eid];
      let flip = 1;
      let a = Sprite.alpha[eid];
      const art = pool.hasAtlasFrame;
      const vx = Velocity.x[eid];
      const vy = Velocity.y[eid];
      const speed2 = vx * vx + vy * vy;
      let shadow = 0;
      // Shadows sit on the floor even when the body bobs or hovers.
      const groundY = y;
      /** Extra squash layered on for the wizard stride / cast (does not mutate Sprite.squash). */
      let motionSquash = 0;
      /** Robe afterimage count drawn behind the main body this frame. */
      let robeTrails = 0;

      switch (id) {
        case SpriteId.Player: {
          shadow = Radius.r[eid];
          const pa = Config.anim.player;
          if (art) {
            flip = Sprite.face[eid] || 1;
            const speed = Math.sqrt(speed2);
            const moving = speed > 0.5;
            // Lean into velocity; flip-aware so a left-facing strafe still reads as tilt.
            const lean = clamp(vx * pa.leanPerVelocity, -pa.maxLean, pa.maxLean);
            const idleSway = Math.sin(t * 5.2) * 0.028;
            rot = idleSway + lean * flip;

            if (moving) {
              // Stride cycle: bounce on the balls of the feet + land-wide / air-tall squash.
              const stride = t * (pa.strideHz + speed * 0.55);
              const phase = Math.sin(stride);
              const bounce = Math.abs(phase);
              y += bounce * pa.bobAmp * (1 + speed * 0.04);
              motionSquash = phase * phase * pa.strideSquash - (1 - bounce) * pa.strideSquash * 0.55;
              // Robe trail: faded copies drifting opposite movement.
              if (speed >= pa.trailMinSpeed) robeTrails = speed > 6 ? 2 : 1;
            } else {
              y += Math.sin(t * 3.4) * 0.02;
            }

            // Cast snap: pull back hard, then thrust toward the aim as recoil decays.
            const kick = Player.recoil[eid];
            if (kick > 0) {
              const u = Math.min(1, kick);
              const pull = u * u;
              const thrust = Math.sin(u * Math.PI);
              const along = thrust * pa.castThrust - pull * pa.castPull;
              x += Player.aimX[eid] * along;
              y += Player.aimY[eid] * along;
              rot += (-pull * 0.14 + thrust * 0.22) * flip;
              motionSquash += pull * 0.35 - thrust * 0.4;
              scale *= 1 + thrust * 0.08;
            }

            // Face-flip anticipation leaves a brief wide squash from AnimationSystem.
          } else {
            rot = Math.atan2(Player.aimY[eid], Player.aimX[eid]) - HALF_PI;
          }
          if (Weapon.overcharge[eid] > 0) {
            const pulse = 0.55 + Math.sin(t * 14) * 0.45;
            tint = mixColor(Sprite.tint[eid] || 0xffffff, OVERCHARGE_TINT, pulse);
            scale *= 1.12 + Math.sin(t * 18) * 0.04;
          } else if (Player.swiftTime[eid] > 0) {
            const pulse = 0.4 + Math.sin(t * 16) * 0.35;
            tint = mixColor(Sprite.tint[eid] || 0xffffff, 0xffe06a, pulse);
            if (speed2 > 4) robeTrails = Math.max(robeTrails, 2);
          } else {
            const frac = Health.hp[eid] / (Health.max[eid] || 1);
            if (frac <= Config.player.lowPowerFraction && !Player.dead[eid]) {
              const critical = frac <= Config.player.criticalPowerFraction;
              const rate = critical ? 16 : 8;
              if ((Math.floor(t * rate) & 1) === 0) {
                tint = critical ? 0xff4242 : 0xff8275;
              }
            }
          }
          if (Player.dashTime[eid] > 0) {
            robeTrails = Math.max(robeTrails, 2);
            tint = mixColor(tint || Sprite.tint[eid] || 0xffffff, 0xc8e8ff, 0.4);
            motionSquash += 0.22;
          }
          break;
        }
        case SpriteId.Imp:
        case SpriteId.Golem:
        case SpriteId.Skull:
        case SpriteId.Slime:
        case SpriteId.Bat:
        case SpriteId.Gargoyle:
        case SpriteId.Wraith:
        case SpriteId.Hexer:
        case SpriteId.Cinder:
        case SpriteId.Blight:
        case SpriteId.Brute:
        case SpriteId.Spitter:
        case SpriteId.Mimic: {
          shadow = Radius.r[eid];
          if (Dying.flag[eid]) {
            if (art && spec.faceFlip) flip = Sprite.face[eid];
            const dur = Math.max(0.01, Anims.duration(id, Anim.clip[eid]));
            sampleDeathVisual(Enemy.kind[eid] as EnemyKind, id, Enemy.mode[eid], Enemy.seed[eid], Math.min(1, Anim.time[eid] / dur));
            y += deathVisual.yOff;
            rot += deathVisual.rot;
            scale *= deathVisual.scale;
            motionSquash += deathVisual.squash;
            a *= deathVisual.alpha;
            if (deathVisual.tintMix > 0) tint = mixColor(tint, deathVisual.tint, deathVisual.tintMix);
            shadow *= deathVisual.shadow;
            break;
          }
          const flyer = id === SpriteId.Skull || id === SpriteId.Bat || id === SpriteId.Cinder || id === SpriteId.Wraith;
          const mimicAsleep = id === SpriteId.Mimic && Enemy.mode[eid] < 2;
          if (art && spec.faceFlip) {
            flip = Sprite.face[eid];
            const golem = id === SpriteId.Golem || id === SpriteId.Gargoyle || id === SpriteId.Brute || id === SpriteId.Spitter;
            rot = mimicAsleep ? 0 : Sprite.rot[eid] * 0.5 + clamp(vx * Config.anim.leanPerVelocity * (golem ? 0.5 : 1.5), -0.25, 0.25);
            // A jolted golem reels away from the blow.
            if (golem) rot -= clamp(Sprite.joltX[eid] * 0.7, -0.3, 0.3);
            if (flyer) {
              // Airborne: constant hover above a smaller, softer shadow. Wraiths ride higher and drift.
              const wraith = id === SpriteId.Wraith;
              const hover = (wraith ? 0.55 : 0.35) + Math.sin(t * (wraith ? 3 : 6) + Enemy.seed[eid] * 12) * (wraith ? 0.14 : 0.08);
              y += hover;
              shadow *= wraith ? 0.45 : 0.7;
            } else if (speed2 > 0.5 && !mimicAsleep) {
              y += Math.abs(Math.sin(t * (golem ? 7 : 22) + Enemy.seed[eid] * 9)) * Config.anim.bobAmp * (golem ? 1.6 : 1);
            }
          } else if (speed2 > 0.05 && !mimicAsleep) {
            rot = Math.atan2(vy, vx) - HALF_PI + Sprite.rot[eid];
          }
          if (id === SpriteId.Cinder) {
            // The fuse: a warm pulse that quickens with the chase ramp.
            const heat = Math.min(1, Enemy.beat[eid] / Config.enemies.cinder.rampSeconds);
            tint = mixColor(tint, 0xff4a10, 0.25 + Math.sin(t * (6 + heat * 12)) * 0.2 + heat * 0.25);
          }
          break;
        }
        case SpriteId.Dragon: {
          shadow = 0;
          // Dorsal art faces +x; the sim steers Sprite.rot toward the heading.
          rot = Sprite.rot[eid];
          const lift = DragonTag.bob[eid];
          y += lift;
          // Higher on the flap means closer to the camera.
          scale *= 1 + lift * 0.06;
          // Wing shadow lags the body by the fake altitude and shrinks as it climbs.
          if (a > 0.05) {
            const shScale = (3.2 - lift * 0.5) * Sprite.scale[eid] * 0.28;
            shadows.push(x + 0.4, y - lift - 1.8, 0.02, rot, shScale * 2.4, shScale * 1.1, SHADOW_TINT, 0.32 * a, 0);
          }
          break;
        }
        case SpriteId.BossLich:
        case SpriteId.BossDruid:
        case SpriteId.BossMagus:
        case SpriteId.BossWarlord:
        case SpriteId.BossSkullKing:
        case SpriteId.BossColossus:
        case SpriteId.BossMatriarch:
        case SpriteId.BossSentinel:
        case SpriteId.BossHexweaver:
        case SpriteId.BossAshmaw:
        case SpriteId.BossBride: {
          // BossSystem owns the altitude (hover bob, crash-down fall, swoop arc, hop);
          // the shadow stays on the floor and shrinks as the body lifts.
          const lift = BossTag.lift[eid];
          shadow = Radius.r[eid] * Math.max(0.2, 0.85 - lift * 0.08);
          y += lift;
          if (art) {
            flip = Sprite.face[eid];
            const lean = id === SpriteId.BossMatriarch || id === SpriteId.BossAshmaw ? 1.2 : id === SpriteId.BossWarlord || id === SpriteId.BossSentinel ? 0.15 : 0.35;
            rot = Sprite.rot[eid] + clamp(vx * Config.anim.leanPerVelocity * lean, -0.3, 0.3) - clamp(Sprite.joltX[eid] * 0.5, -0.2, 0.2);
          } else {
            rot = Sprite.rot[eid] + Math.sin(t * 2.2) * 0.06;
          }
          if (BossTag.enraged[eid] && !Dying.flag[eid]) {
            // Enrage: the body pulses toward its spell colour.
            const pulse = 0.35 + Math.sin(t * 7) * 0.25;
            tint = mixColor(tint, bossDef(BossTag.kind[eid]).glowTint, pulse);
          }
          if (Dying.flag[eid]) {
            // Hold the body through the crescendo, then the final flash takes it.
            const u = Anim.time[eid] / Math.max(0.01, Anims.duration(id, Anim.clip[eid]));
            a *= 1 - Math.max(0, (u - 0.8) * 5);
            shadow *= Math.max(0, 1 - u);
            motionSquash += u * 0.28;
            if (u > 0.45) tint = mixColor(tint, 0x2a1010, (u - 0.45) * 0.42);
          }
          break;
        }
        case SpriteId.BossRubble:
          // Tumbling stone: the sim spins Sprite.rot; the fallback is a plain chunk.
          rot = Sprite.rot[eid];
          shadow = 0.18;
          break;
        case SpriteId.BossAcid:
          rot = t * 3 + eid;
          shadow = 0.2;
          break;
        case SpriteId.BossSkull:
          // Launched skulls point along their flight; parked ones face the wizard side.
          if (speed2 > 0.05) flip = vx < 0 ? -1 : 1;
          y += 0.25 + Math.sin(t * 8 + eid) * 0.06;
          break;
        case SpriteId.BossBolt:
          if (art) rot = Sprite.rot[eid];
          break;
        case SpriteId.BossOrb:
        case SpriteId.BossWisp: {
          // Orbs spin; the procedural fallback breathes so they read as live magic.
          rot = t * (id === SpriteId.BossOrb ? 4 : -6) + eid;
          if (!art) scale *= 1 + Math.sin(t * 12 + eid) * 0.1;
          break;
        }
        case SpriteId.Portal: {
          // Hit squash reads as a rim flex. Wound drains colour, critical gates flicker,
          // and a dying mouth implodes instead of popping off.
          const maxHp = Health.max[eid] || 1;
          const wound = 1 - clamp(Health.hp[eid] / maxHp, 0, 1);
          const theme = floorThemeDef(this.ctx.floor.floor).portal;
          tint = mixColor(Sprite.tint[eid] || 0xffffff, theme, 0.28);
          if (wound > 0.18) tint = mixColor(tint, PORTAL_WOUNDED, clamp((wound - 0.18) * 1.35, 0, 1));
          if (wound > 0.6) tint = mixColor(tint, PORTAL_CRITICAL, clamp((wound - 0.6) / 0.4, 0, 1));
          scale *= 1 + Sprite.squash[eid] * 0.16;
          shadow = Radius.r[eid] * 1.4;
          if (!Dying.flag[eid] && wound > 0.65) {
            const flicker = 0.5 + 0.5 * Math.max(0, Math.sin(t * 23 + eid) * (Math.sin(t * 41 + eid * 1.7) > 0.15 ? 1 : 0.25));
            a *= 0.55 + flicker * 0.45;
            scale *= 1 + Math.sin(t * 19 + eid) * 0.055 * wound;
          } else if (wound > 0.32 && !Dying.flag[eid]) {
            a *= 0.88 + Math.sin(t * 9 + eid) * 0.06;
          }
          if (Dying.flag[eid]) {
            const u = clamp(Anim.time[eid] / Math.max(0.01, Anims.duration(id, Anim.clip[eid])), 0, 1);
            scale *= 1 - u * 0.42;
            a *= 1 - Math.max(0, (u - 0.55) * 2.2);
            tint = mixColor(tint, 0xffe8ff, Math.min(1, u * 1.4));
          }
          break;
        }
        case SpriteId.Ghost:
          rot = Math.sin(t * 2) * 0.2;
          y += Math.sin(t * 3) * 0.1;
          break;
        case SpriteId.Mine: {
          // Planted on the floor. Pulse only; fuse strobe lives on Sprite.flash.
          y -= 0.08;
          scale *= 1 + Math.sin(t * 8 + eid) * 0.06;
          shadow = 0.4;
          break;
        }
        case SpriteId.WardBlade:
          rot = t * 3.2 + eid;
          shadow = 0.15;
          break;
        case SpriteId.PickupHeart: {
          // Heartbeat: two quick swells per cycle.
          const beat = Math.pow(Math.max(0, Math.sin(t * 5 + eid)), 6) + 0.6 * Math.pow(Math.max(0, Math.sin(t * 5 + eid + 0.9)), 6);
          scale *= 1 + beat * 0.22;
          y += Math.sin(t * 3 + eid) * 0.1;
          rot = Math.sin(t * 2 + eid) * 0.1;
          shadow = 0.3;
          break;
        }
        case SpriteId.Gem:
        case SpriteId.PickupCleaver:
        case SpriteId.PickupLightning:
        case SpriteId.PickupCrossbow:
        case SpriteId.PickupBomb:
        case SpriteId.PickupShield:
        case SpriteId.PrizeCabbage:
        case SpriteId.PrizeHat:
        case SpriteId.PrizeChalice:
        case SpriteId.PrizeGoldChest:
        case SpriteId.PrizeSpellbook:
        case SpriteId.PrizeCrystalBall:
        case SpriteId.PrizePotion:
        case SpriteId.PrizeHourglass: {
          y += Math.sin(t * 4 + eid) * 0.08;
          rot = Math.sin(t * 3 + eid) * 0.12;
          shadow = 0.3;
          break;
        }
        case SpriteId.BoltWand:
        case SpriteId.BoltFire:
        case SpriteId.BoltPhase:
          if (art) rot = Sprite.rot[eid];
          break;
        case SpriteId.Slash:
        case SpriteId.FlameJet:
          if (art) rot = Sprite.rot[eid];
          break;
        default:
          break;
      }

      // Procedural deformation shared by everything.
      const squash = Sprite.squash[eid] + motionSquash;
      const pop = Sprite.pop[eid];
      // Impact jolt shoves the body (not the shadow) along the hit; the flash whites it out.
      const jx = Sprite.joltX[eid];
      const jy = Sprite.joltY[eid];
      const flash = Dying.flag[eid] ? Sprite.flash[eid] * 0.55 : Sprite.flash[eid];
      if (!art && flash > 0.3) tint = 0xffffff;
      let sx = scale * (1 + squash * 0.3) * (1 + pop * 0.45);
      let sy = scale * (1 - squash * 0.3) * (1 + pop * 0.45);
      if (pop > 0.001) a *= Math.min(1, 1.35 - pop);

      let frameIndex = 0;
      if (art) {
        const clip = Anims.clip(id, Anim.clip[eid]);
        frameIndex = clip.start + Math.min(Anim.frame[eid], clip.len - 1);
        const f = pool.frames[frameIndex];
        const unit = 1 / f.ppu;
        // Pivot: the frame center sits offset from the entity position.
        const cx = -f.ox * unit * flip * sx;
        const cy = f.oy * unit * sy;
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);
        x += cx * cos - cy * sin;
        y += cx * sin + cy * cos;
        sx *= f.w * unit;
        sy *= f.h * unit;
      } else {
        sx *= spec.aspect;
      }

      if (shadow > 0 && a > 0.05) {
        shadows.push(x, groundY - shadow * 0.85 - (id === SpriteId.Player ? 0.28 : 0.15), 0.02, 0, shadow * 2.6, shadow * 1.15, SHADOW_TINT, 0.4 * a, 0);
      }
      if (id === SpriteId.Player && robeTrails > 0 && speed2 > 0.5) {
        // Robe drift: soft afterimages lag opposite velocity so the cloak reads in motion.
        const inv = 1 / Math.sqrt(speed2);
        const bx = vx * inv;
        const by = vy * inv;
        const spacing = Config.anim.player.trailSpacing;
        for (let k = 1; k <= robeTrails; k++) {
          const back = k * spacing;
          const fade = 0.28 - k * 0.1;
          pool.push(
            x - bx * back + jx * 0.3,
            y - by * back + jy * 0.3,
            Sprite.z[eid] - 0.02 * k,
            rot * (1 - k * 0.15),
            sx * flip * (1 - k * 0.08),
            sy * (1 + k * 0.04),
            tint,
            a * fade,
            frameIndex,
            0,
            groundY,
          );
        }
      }
      if (id === SpriteId.BoltPhase && speed2 > 1) {
        // Phase arrows leave fading afterimages behind them.
        const inv = 1 / Math.sqrt(speed2);
        const bx = vx * inv;
        const by = vy * inv;
        for (let k = 1; k <= 3; k++) {
          const back = k * 0.55;
          pool.push(x - bx * back, y - by * back, Sprite.z[eid] - 0.01, rot, sx * flip * (1 - k * 0.12), sy * (1 - k * 0.12), tint, a * (0.45 - k * 0.12), frameIndex);
        }
      }
      // Wand-tip flare while casting / overcharged — sits ahead of the body along aim.
      if (id === SpriteId.Player && !Player.dead[eid] && (Player.firing[eid] || Weapon.overcharge[eid] > 0 || Player.recoil[eid] > 0.05)) {
        const reach = Config.anim.player.muzzleReach;
        const kick = Player.recoil[eid];
        const tipX = Position.x[eid] + Player.aimX[eid] * (reach + kick * 0.08);
        const tipY = Position.y[eid] + Player.aimY[eid] * (reach + kick * 0.08);
        const oc = Weapon.overcharge[eid] > 0;
        const flare = 0.22 + (Player.firing[eid] ? 0.12 : 0) + kick * 0.18 + (oc ? 0.14 : 0);
        const tipPool = pools[SpriteId.Spark];
        const weaponTint = weaponDef(Weapon.typeId[eid]).tint;
        const tipTint = oc ? OVERCHARGE_TINT : weaponTint;
        tipPool.push(tipX, tipY, Sprite.z[eid] + 0.05, t * 9, flare, flare, tipTint, 0.75 + kick * 0.2, 0);
      }
      // Sort key is the ground position: bob, hover and the frame pivot must not reorder neighbours.
      pool.push(x + jx, y + jy, Sprite.z[eid], rot, sx * flip, sy, tint, a, frameIndex, flash, groundY);
      if (id === SpriteId.Portal) this.pushPortalLayers(eid, pool, x + jx, y + jy, rot, sx, sy, tint, a, frameIndex, flash, art, t);
    }

    this.pushTorches(t);
    this.portalAura.end();
    for (let i = 0; i < pools.length; i++) pools[i].end();
  }

  /**
   * Extra swirl copies plus the additive aura. Colour, crack amount, and collapse
   * follow the portal's remaining health so a wounded gate reads before it dies.
   */
  private pushPortalLayers(
    eid: number,
    pool: SpritePool,
    x: number,
    y: number,
    rot: number,
    sx: number,
    sy: number,
    tint: number,
    a: number,
    frameIndex: number,
    flash: number,
    art: boolean,
    t: number,
  ): void {
    const maxHp = Health.max[eid] || 1;
    const wound = Dying.flag[eid] ? 1 : 1 - clamp(Health.hp[eid] / maxHp, 0, 1);
    const interval = Math.max(0.08, Spawner.interval[eid]);
    const charge = Dying.flag[eid] ? 0 : 1 - clamp(Spawner.timer[eid] / interval, 0, 1);
    const deathU = Dying.flag[eid] ? clamp(Anim.time[eid] / Math.max(0.01, Anims.duration(SpriteId.Portal, Anim.clip[eid])), 0, 1) : 0;
    const theme = floorThemeDef(this.ctx.floor.floor).portal;
    let auraTint = mixColor(0xc46bff, theme, 0.5);
    if (wound > 0.2) auraTint = mixColor(auraTint, 0xe09030, clamp((wound - 0.2) * 1.1, 0, 1));
    if (wound > 0.6) auraTint = mixColor(auraTint, 0xff3355, clamp((wound - 0.6) / 0.4, 0, 1));
    if (flash > 0.2) auraTint = mixColor(auraTint, 0xffffff, clamp(flash, 0, 0.75));

    let ghostFrame = frameIndex;
    if (art) {
      const clip = Anims.clip(SpriteId.Portal, Anim.clip[eid]);
      ghostFrame = clip.start + ((Anim.frame[eid] + 3) % Math.max(1, clip.len));
    }
    const ghostA = a * (0.42 - wound * 0.08) * (1 - deathU * 0.7);
    if (ghostA > 0.04) {
      pool.push(x, y, Sprite.z[eid] - 0.02, -rot * 1.35, sx * 1.28, sy * 1.28, auraTint, ghostA, ghostFrame, flash * 0.35);
      pool.push(x, y, Sprite.z[eid] - 0.03, rot * 0.55 + t * 0.4, sx * 0.68, sy * 0.68, mixColor(tint, 0xffffff, 0.35), ghostA * 1.2, frameIndex, 0);
    }

    const auraScale = Sprite.scale[eid] * (1.95 + charge * 0.22) * (1 - deathU * 0.55);
    const auraAlpha = (0.92 + charge * 0.2) * a * (1 - deathU * 0.15);
    this.portalAura.push(x, y, 0.055, 0, auraScale, auraTint, auraAlpha, wound, flash, charge, deathU);
  }

  /** Wall torches are scenery, not entities: animate the flame clip straight from time. */
  private pushTorches(t: number): void {
    const pool = this.pools[SpriteId.Torch];
    const clip = Anims.clip(SpriteId.Torch, 0);
    this.drawTorchSet(this.arena, 0, 0, t, pool, clip);
    const room = this.ctx.room;
    if (this.nextArena && room.phase === RoomPhase.Transition) {
      this.drawTorchSet(this.nextArena, room.shiftX, room.shiftY, t, pool, clip);
    }
  }

  private drawTorchSet(
    arena: ArenaScene,
    ox: number,
    oy: number,
    t: number,
    pool: SpritePool,
    clip: { len: number; start: number; fps: number },
  ): void {
    for (let i = 0; i < arena.torchCount; i++) {
      const x = arena.torchFlames[i * 2] + ox;
      const y = arena.torchFlames[i * 2 + 1] + oy;
      const frame = clip.len > 1 ? clip.start + (Math.floor(t * clip.fps + i * 1.7) % clip.len) : 0;
      const flicker = 1 + Math.sin(t * 13 + i * 2.3) * 0.08;
      let sx = 0.7 * flicker;
      let sy = 0.7 * flicker;
      if (pool.hasAtlasFrame) {
        const f = pool.frames[frame];
        sx *= f.w / f.ppu;
        sy *= f.h / f.ppu;
      } else {
        sx *= 0.6;
      }
      pool.push(x, y + 0.2, 0.12, 0, sx, sy, 0xffffff, 0.95, frame);
    }
  }

  private syncBeams(): void {
    const b = this.ctx.beams;
    this.beams.begin();
    const players = query(this.ctx.world, [Player, Weapon]);
    let thick = 1;
    for (let i = 0; i < players.length; i++) {
      if (Weapon.overcharge[players[i]] > 0) thick = 1.8;
    }
    // Lightning jitters per frame so the beam crackles instead of sitting still.
    const jitter = 0.85 + Math.sin(this.ctx.time * 61) * 0.2 + Math.sin(this.ctx.time * 137) * 0.1;
    for (let i = 0; i < b.count; i++) {
      const o = i * 4;
      this.beams.push(b.data[o], b.data[o + 1], b.data[o + 2], b.data[o + 3], thick * jitter);
    }
    this.beams.end();
  }

  /** Exposed so InputSystem can map the mouse into world space. */
  screenToWorld(sx: number, sy: number, out: Float32Array): void {
    this.arenaCamera.screenToWorld(sx, sy, out);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Per-channel lerp of two 0xRRGGBB colours (no allocation). */
function mixColor(a: number, b: number, u: number): number {
  const k = clamp(u, 0, 1);
  const r = ((a >> 16) & 0xff) + ((((b >> 16) & 0xff) - ((a >> 16) & 0xff)) * k) | 0;
  const g = ((a >> 8) & 0xff) + ((((b >> 8) & 0xff) - ((a >> 8) & 0xff)) * k) | 0;
  const bl = (a & 0xff) + (((b & 0xff) - (a & 0xff)) * k) | 0;
  return (r << 16) | (g << 8) | bl;
}
